import { UNAUTHORIZED_MESSAGE } from '../constants';
import { removeJWTFromLocalStorage } from '../utilities/jwt';
import { heapTrackUnauthorized, logError } from '../utilities/logging';

const WEB_AUTH_API_BASE_URL = '/auth/api';

export interface Address {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  stateOfResidence: string;
}

export interface PhoneNumber {
  country_code: string;
  area_code: string;
  phone_number: string;
  phone_type_id: number;
  preferred_contact: boolean;
  visible: boolean;
  verified: boolean;
}

export interface PersonalInfo {
  user_name_type_id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  user_name_type: string;
  user_other_names: {
    LEGAL_NAME: string;
  };
}

export interface AddressInfo {
  addressAsString: string;
  address_1: string;
  city: string;
  state: string;
  zip_code: string;
  address_type_id: number;
  source_type_id: number;
  state_of_residence: string;
  is_legal_address: boolean;
  allow_po_box_address: boolean;
}

export interface ContactInfo {
  email: string;
  phone_numbers: PhoneNumber[];
}

export interface LendingAccreditation {
  prospectus_reviewed: boolean;
  lender_registration_agreement_approved: boolean;
  ira_partners_fee_account_signed: boolean;
  credit_profile_auth: boolean;
  terms_of_use: boolean;
  consent_to_electronic_disclosures: boolean;
  privacy_policies_agreement: boolean;
  lending_accreditation_requirement_type_id: number;
  is_tax_withholding_approved: boolean;
  data_terms_of_use: boolean;
  provider_info_sharing: boolean;
}

export interface EmploymentInfo {
  employer_id: number;
  employment_month: number;
  employment_year: number;
  employment_status_id: number;
  annual_income: number;
  monthly_income: number;
  is_self_employed: boolean;
  is_income_verifiable: boolean;
  employment_detail_id: number;
}

export interface User {
  type: string;
  role: string;
  is_registration_complete: boolean;
  user_id: number;
  status: string;
  creation_date: string;
  email_status: string;
  alt_key: string;
  is_whole_loans_investor: boolean;
  external_user_id: string;
  roles: string;
  personal_info: PersonalInfo;
  address_info: AddressInfo;
  contact_info: ContactInfo;
  lending_accreditation: LendingAccreditation;
  employment_info: EmploymentInfo;
  credit_grade: number;
  monthly_debt: number;
  pre_reg_info: Record<string, unknown>;
  is_password_set: boolean;
}

export interface UserPayload {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  password: string;
  phoneNumber: string;
  annualIncome: string;
  additionalIncome: string | null;
  tcpaConsent: boolean;
  webBankPrivacyPolicyConsent: boolean;
  address: Address;
  employmentStatus: string | undefined;
}

export interface UserResponse {
  external_user_id: string;
  token: string;
  user: User;
}

export const fetchWebAuth = async (
  method: 'POST' | 'GET',
  endpoint: string,
  body?: any
): Promise<Object> => {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const response = await fetch(`${WEB_AUTH_API_BASE_URL}${endpoint}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let resultText;
  let returnResult;
  switch (response.status) {
    case 504: {
      const err = new Error('Connection to server timed out');
      logError(err, {
        endpoint: `${WEB_AUTH_API_BASE_URL}${endpoint}`,
        statusCode: `${response.status}`,
      });
      throw err;
    }
    case 524: {
      const err = new Error(
        'Connection between CloudFlare and server timed out'
      );
      logError(err, {
        endpoint: `${WEB_AUTH_API_BASE_URL}${endpoint}`,
        statusCode: `${response.status}`,
      });
      throw err;
    }
    case 401: {
      // Track UNAUTHORIZED errors in Heap only
      heapTrackUnauthorized({
        endpoint: `${WEB_AUTH_API_BASE_URL}${endpoint}`,
        statusCode: `${response.status}`,
      });
      // construct a return url from the current url
      const returnURL = `${window.location.pathname}${window.location.search}`;
      // remove the invalid JWT
      removeJWTFromLocalStorage();
      // send them to the email-code sign in page
      window.location.assign(
        `/auth/email-code?redirect_uri=${encodeURIComponent(returnURL)}`
      );
      // throw the unauth error so any consumer can react appropriately
      throw new Error(UNAUTHORIZED_MESSAGE);
    }
    case 403: {
      const err = new Error('Request forbidden');
      logError(err, {
        endpoint: `${WEB_AUTH_API_BASE_URL}${endpoint}`,
        statusCode: `${response.status}`,
      });
      throw err;
    }
    default: {
      resultText = await response.text();
      try {
        returnResult = JSON.parse(resultText);
      } catch (err) {
        returnResult = {};
      }
      return returnResult;
    }
  }
};

export const userExistByEmail = async (email: string): Promise<Object> =>
  fetchWebAuth('POST', '/user-email', { email });
export const createUser = async (user: UserPayload): Promise<UserResponse> =>
  fetchWebAuth('POST', '/user', user) as Promise<UserResponse>;
